/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-12-26 14:52:22                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-27 13:23:27                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * Services
  * SelectCompinfoForm: compinfo select type form component
    * useEffect: keep dataState update
    * useEffect: get compinfo list
    * handleChange: handle choices change
    * displayCompInfoSelect: display proper compinfo select
*/

/* Imports */
import { useEffect, useState } from "react";
/***/

/* Components */
import Loading from "../../loading/Loading";
import CompInfoSelectCheckbox from "./select/CompInfoSelectCheckbox";
import CompInfoSelectRadio from "./select/CompInfoSelectRadio";
import CompInfoSelectSimple from "./select/CompInfoSelectSimple";
import CompInfoSelectMulti from "./select/CompInfoSelectMulti";
/***/

/* Services */
import { api } from "../../../services/api/api";
import { validator } from "../../../services/form-validator/form-validator";
import { allowedCompInfoUsages, isCompInfoUsable } from "./compinfo-usage";
import { getCompInfoLabel } from "./compinfo-errors";
/***/

/**
* SelectCompinfoForm: compinfo select type form component
* @param data - for editiong, current value
* @param onChange - on data change event
* @param onSubmit - on submit event
* @param submitted - submitted state
* @param ressource - compinfo ressource type
* @param status - statut de l'intervention, absent à la création
*/
export default function SelectCompinfoForm({
  data,
  onChange,
  onSubmit,
  submitted,
  ressource,
  status
}: {
  data?: EasyTech.SelectCompInfoValue[]
  onChange: (e: EasyTech.SelectCompInfoValue[]) => void
  onSubmit: () => void
  submitted?: boolean,
  ressource: string,
  status?: string
}) {
  const [ciList, setCiList] = useState<EasyTech.CompInfoWithChoices[]>();
  const [dataState, setDataState] = useState<EasyTech.SelectCompInfoValue[]>(data || []);

  /**
  * useEffect: keep dataState update
  * @param data - reload on data change
  */
  useEffect(() => {
    setDataState(data || []);
  }, [data]);
  /***/

  /**
  * useEffect: get compinfo list
  * @param ressource - reload on ressource change
  */
  useEffect(() => {
    api.get("users/compinfos/selects")
      .then((ret) => {
        // Voir compinfo-usage : le statut dit quels moments de saisie sont ouverts
        const usages = allowedCompInfoUsages(status);
        const filteredList = (ret as EasyTech.CompInfoWithChoices[])
          .filter((el) => el.service === ressource && isCompInfoUsable(el, usages));
          
        // Tri par order (propriété order du compInfo)
        const sortedList = filteredList.sort((a, b) => {
          if (a.order === undefined) return 1;
          if (b.order === undefined) return -1;
          return a.order - b.order;
        });
        
        setCiList(sortedList);
      }).catch((err) => {
        console.error(err);
        setCiList([]);
      });
  }, [ressource, status]);
  /***/

  /**
  * handleChange: handle choices change
  * @param ci - current compinfo
  * @param choices - new choices 
  */
  const handleChange = (ci: EasyTech.CompInfoWithChoices, choices: EasyTech.CompInfoChoice[]) => {
    const ret = [...dataState];
    const i = ret.findIndex((el) => el.compInfo && el.compInfo.id === ci.id);

    const existingCompinfo = dataState.find((el) => el.compInfo && el.compInfo.id === ci.id);

    const tmp: any = {
      compInfo: ci,
      values: choices.map((el) => ({value: el}))
    };
    
    if (existingCompinfo && existingCompinfo.id) {
      tmp.id = existingCompinfo.id;
    }

    if (i !== -1) ret[i] = tmp;
    else ret.push(tmp);
    
    onChange(ret);
  };
  /***/

  /**
  * displayCompInfoSelect: display proper compinfo select
  * @param ci - compinfo descriptor
  * @return - Compinfo select
  */
  const displayCompInfoSelect = (ci: EasyTech.CompInfoWithChoices) => {
    const i = dataState.findIndex((el) => el.compInfo && el.compInfo.id === ci.id);
    let values: EasyTech.CompInfoChoice[] = [];
    
    try {
      if (i !== -1 && dataState[i] && dataState[i].values && Array.isArray(dataState[i].values)) {
        values = dataState[i].values
          .filter(item => item && item.value)
          .map(item => item.value);
      }
    } catch (error) {
      console.error("Error processing values:", error);
    }

    if (ci.type === "CHECKBOX")
      return <CompInfoSelectCheckbox  className={`${(submitted && !validator.isValidSelectCompInfo(ci, values)) && "error"}`}
                                      choices={ci.choices}
                                      values={values}
                                      onChange={(e) => handleChange(ci, e)}/>;
    if (ci.type === "RADIO")
      return <CompInfoSelectRadio className={`${(submitted && !validator.isValidSelectCompInfo(ci, values)) && "error"}`}
                                  id={ci.id}
                                  choices={ci.choices}
                                  values={values}
                                  onChange={(e) => handleChange(ci, e)}/>;
    if (ci.type === "SELECT")
      return <CompInfoSelectSimple  className={`${(submitted && !validator.isValidSelectCompInfo(ci, values)) && "error"}`}
                                    choices={ci.choices}
                                    values={values}
                                    onChange={(e) => handleChange(ci, e)}/>;
    if (ci.type === "MULTIPLESELECT")
      return <CompInfoSelectMulti className={`${(submitted && !validator.isValidSelectCompInfo(ci, values)) && "error"}`}
                                  choices={ci.choices}
                                  values={values}
                                  onChange={(e) => handleChange(ci, e)}/>;
  };
  /***/

  return (
    <div className="contents">
      {ciList ?
        <>
          {ciList.map((ci) => (
            <div key={ci.id} className="field" style={{ order: ci.order ?? 9999 }}>
              <label>{getCompInfoLabel(ci)} {ci.constraints === "REQUIRED" && "*"}</label>
              {displayCompInfoSelect(ci)}
            </div>
          ))}
        </>
      : <Loading size="h-5 w-5"/>}
    </div>
  );
}
/***/
